<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-8 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t('reprocess.title') || 'Reprocess Orders' }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-breadcrumb />
          </nav>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <card>
        <!-- Filter row -->
        <div class="row align-items-end">
          <div class="col-md-6">
            <base-input :label="$t('reprocess.status_filter') || 'Status'">
              <el-select
                v-model="filters.statusList"
                multiple
                clearable
                collapse-tags
                style="width: 100%"
                @change="loadData">
                <el-option v-for="opt in statusOptions" :key="opt.value" :label="opt.label" :value="opt.value"></el-option>
              </el-select>
            </base-input>
          </div>
          <div class="col-md-2">
            <base-input :label="$t('reprocess.platform') || 'Platform'">
              <el-select v-model="filters.platformID" clearable @change="loadData" style="width: 100%">
                <el-option :label="$t('reprocess.platform_all') || 'ทั้งหมด'" :value="0"></el-option>
                <el-option label="Madamefin" :value="1"></el-option>
                <el-option label="TikTok" :value="2"></el-option>
                <el-option label="Shopee" :value="3"></el-option>
                <el-option label="Lazada" :value="4"></el-option>
                <el-option label="Line" :value="5"></el-option>
                <el-option label="Pancake" :value="8"></el-option>
                <el-option label="MDFStore" :value="9"></el-option>
              </el-select>
            </base-input>
          </div>
          <div class="col-md-2">
            <base-input :label="$t('reprocess.carrier') || 'ขนส่ง'">
              <el-select v-model="filters.shippingProviderID" clearable @change="loadData" style="width: 100%">
                <el-option :label="$t('reprocess.platform_all') || 'ทั้งหมด'" :value="0"></el-option>
                <el-option label="ไปรษณีย์ไทย" :value="1"></el-option>
                <el-option label="Kerry" :value="2"></el-option>
                <el-option label="NinjaVan" :value="3"></el-option>
                <el-option label="Flash" :value="4"></el-option>
                <el-option label="DHL" :value="5"></el-option>
                <el-option label="MadameFin Express" :value="6"></el-option>
                <el-option label="SPX" :value="7"></el-option>
              </el-select>
            </base-input>
          </div>
          <div class="col-md-2">
            <base-input :label="$t('reprocess.created_range') || 'Created date'">
              <el-date-picker
                v-model="filters.createdRange"
                type="daterange"
                range-separator="—"
                value-format="yyyy-MM-dd"
                format="yyyy-MM-dd"
                clearable
                @change="loadData"
                style="width: 100%"
              ></el-date-picker>
            </base-input>
          </div>
        </div>

        <!-- Action bar -->
        <div class="row mt-2 align-items-center">
          <div class="col-md-12">
            <base-button size="sm" type="primary" :loading="loading" @click="loadData">
              <i class="fa fa-search"></i> {{ $t('search') || 'Search' }}
            </base-button>
            <base-button size="sm" type="success" :disabled="selected.length === 0 || bulkRunning" :loading="bulkRunning" @click="refreshSelected" class="ml-2">
              <i class="fa fa-sync"></i>
              {{ $t('reprocess.refresh_selected') || 'Refresh selected' }} ({{ selected.length }})
            </base-button>
            <base-button size="sm" type="warning" :disabled="batchRunning" :loading="batchRunning" @click="startBatch" class="ml-2">
              <i class="fa fa-rocket"></i>
              {{ $t('reprocess.process_all') || 'Process all matching' }}
            </base-button>
            <base-button size="sm" type="info" @click="showTrackCount" class="ml-2">
              <i class="fa fa-tachometer-alt"></i>
              {{ $t('reprocess.track_count_btn') || 'โควต้า Thaipost' }}
            </base-button>
            <!-- Admin-only buttons -->
            <template v-if="isAdmin">
              <base-button size="sm" type="danger" @click="openFixStatusModal" class="ml-2">
                <i class="fa fa-wrench"></i>
                {{ $t('reprocess.fix_status.title') || 'แก้ไขสถานะ Order' }}
              </base-button>
              <base-button size="sm" type="default" @click="openSubscribeModal" class="ml-2">
                <i class="fa fa-bell"></i>
                {{ $t('reprocess.thaipost_subscribe.title') || 'Subscribe Tracking (Thaipost)' }}
              </base-button>
            </template>
          </div>
        </div>
        <div class="row mt-1">
          <div class="col-md-12 text-right">
            <span class="text-muted">{{ $t('reprocess.total') || 'Total' }}: {{ total }}</span>
          </div>
        </div>

        <!-- Result table -->
        <div class="row mt-3">
          <div class="col-md-12">
            <el-table
              :data="rows"
              v-loading="loading"
              style="width: 100%"
              @selection-change="onSelectionChange">
              <el-table-column type="selection" width="42"></el-table-column>
              <el-table-column prop="ID" :label="$t('reprocess.order_id') || 'Order ID'" min-width="25"></el-table-column>
              <el-table-column prop="shipping_provider_name" :label="$t('reprocess.carrier') || 'Carrier'" min-width="40"></el-table-column>
              <el-table-column prop="shipping_tracking_number" :label="$t('reprocess.tracking') || 'Tracking'" min-width="40"></el-table-column>
              <el-table-column prop="order_status" :label="$t('reprocess.order_status') || 'Status'" min-width="50">
                <template slot-scope="{row}">{{ statusLabel(row.order_status) }}</template>
              </el-table-column>
              <el-table-column prop="shipping_status_name" :label="$t('reprocess.shipping_status') || 'Shipping status'" min-width="50"></el-table-column>
              <el-table-column prop="updated_at" :label="$t('reprocess.updated_at') || 'Updated'" min-width="30">
                <template slot-scope="{row}">{{ formatDateTime(row.updated_at) }}</template>
              </el-table-column>
              <el-table-column :label="$t('reprocess.action') || 'Action'" width="140" align="center">
                <template slot-scope="{row}">
                  <base-button
                    type="primary"
                    size="sm"
                    :loading="rowLoading[row.ID]"
                    @click="refreshRow(row)">
                    <i class="fa fa-sync"></i> {{ $t('reprocess.refresh') || 'Refresh' }}
                  </base-button>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </div>

        <!-- Pagination -->
        <div slot="footer" class="d-flex justify-content-between mt-3" v-if="total > 0">
          <span class="text-muted">{{ from + 1 }} — {{ to }} {{ $t('of') || 'of' }} {{ total }}</span>
          <el-pagination
            :current-page.sync="page"
            :page-size="perPage"
            :total="total"
            layout="prev, pager, next"
            @current-change="loadData">
          </el-pagination>
        </div>
      </card>
    </div>

    <!-- Batch progress modal — tracking refresh (Process all / Refresh selected) -->
    <modal :show.sync="batchModalShow" modal-classes="modal-secondary">
      <h3 slot="header" class="modal-title">
        {{ $t('reprocess.batch_title') || 'Bulk refresh progress' }}
      </h3>
      <div v-if="batchState">
        <div class="progress mb-3" style="height: 24px;">
          <div
            class="progress-bar bg-success"
            role="progressbar"
            :style="{ width: batchPercent + '%' }">
            {{ batchPercent }}%
          </div>
        </div>
        <div class="row text-center">
          <div class="col">
            <small class="text-muted d-block">{{ $t('reprocess.batch_total') || 'Total' }}</small>
            <h4>{{ batchState.total || 0 }}</h4>
          </div>
          <div class="col">
            <small class="text-muted d-block">{{ $t('reprocess.batch_processed') || 'Processed' }}</small>
            <h4>{{ batchState.processed || 0 }}</h4>
          </div>
          <div class="col">
            <small class="text-muted d-block text-warning">{{ $t('reprocess.batch_remaining') || 'Remaining' }}</small>
            <h4 class="text-warning">{{ batchRemaining }}</h4>
          </div>
          <div class="col">
            <small class="text-muted d-block text-success">{{ $t('reprocess.batch_success') || 'Success' }}</small>
            <h4 class="text-success">{{ batchState.success || 0 }}</h4>
          </div>
          <div class="col">
            <small class="text-muted d-block text-danger">{{ $t('reprocess.batch_fail') || 'Failed' }}</small>
            <h4 class="text-danger">{{ batchState.fail || 0 }}</h4>
          </div>
        </div>
        <div v-if="batchState.running" class="text-muted mt-2 text-center">
          <i class="fa fa-spinner fa-spin"></i>
          {{ $t('reprocess.batch_running') || 'Running' }} —
          {{ $t('reprocess.batch_current') || 'order' }} #{{ batchState.current_id || '-' }}
        </div>
        <div v-else-if="batchState.finished_at" class="text-success mt-2 text-center">
          <i class="fa fa-check-circle"></i>
          {{ $t('reprocess.batch_done') || 'Completed' }}
        </div>
        <div v-if="batchState.errors && batchState.errors.length > 0" class="mt-3">
          <small class="text-muted">{{ $t('reprocess.batch_errors') || 'Recent errors (first 50)' }}:</small>
          <ul class="list-unstyled small text-danger mb-0" style="max-height: 160px; overflow-y: auto;">
            <li v-for="e in batchState.errors" :key="e.order_id">
              <code>{{ e.order_id }}</code> — {{ e.message }}
            </li>
          </ul>
        </div>
      </div>
      <template slot="footer">
        <base-button type="link" :disabled="batchState && batchState.running" @click="closeBatchModal">
          {{ $t('close') || 'Close' }}
        </base-button>
      </template>
    </modal>

    <!-- Thaipost daily-quota modal — read-only snapshot from Redis cache. -->
    <modal :show.sync="trackCountModalShow" modal-classes="modal-secondary">
      <h3 slot="header" class="modal-title">
        {{ $t('reprocess.track_count_title') || 'โควต้า Thaipost วันนี้' }}
      </h3>
      <div v-if="trackCount && trackCount.track_count_limit > 0">
        <div class="progress mb-3" style="height: 24px;">
          <div
            class="progress-bar"
            :class="trackCountPercent >= 90 ? 'bg-danger' : trackCountPercent >= 70 ? 'bg-warning' : 'bg-success'"
            role="progressbar"
            :style="{ width: trackCountPercent + '%' }">
            {{ trackCountPercent }}%
          </div>
        </div>
        <div class="row text-center">
          <div class="col">
            <small class="text-muted d-block">{{ $t('reprocess.track_count_used') || 'ใช้ไปแล้ว' }}</small>
            <h4>{{ trackCount.count_number }}</h4>
          </div>
          <div class="col">
            <small class="text-muted d-block text-warning">{{ $t('reprocess.track_count_remaining') || 'คงเหลือ' }}</small>
            <h4 class="text-warning">{{ trackCount.track_count_limit - trackCount.count_number }}</h4>
          </div>
          <div class="col">
            <small class="text-muted d-block">{{ $t('reprocess.track_count_limit') || 'Limit' }}</small>
            <h4>{{ trackCount.track_count_limit }}</h4>
          </div>
        </div>
        <div class="text-muted small mt-3 text-center">
          <span>{{ $t('reprocess.track_count_date') || 'วันที่' }}: {{ trackCount.track_date }}</span>
          <span class="ml-3">{{ $t('reprocess.track_count_updated') || 'อัพเดท' }}: {{ formatDateTime(trackCount.updated_at) }}</span>
        </div>
      </div>
      <div v-else class="text-muted text-center py-3">
        {{ $t('reprocess.track_count_empty') || 'ยังไม่มีข้อมูล — ต้องเรียก refresh อย่างน้อย 1 ครั้งก่อน' }}
      </div>
      <template slot="footer">
        <base-button type="primary" :loading="trackCountLoading" @click="showTrackCount">
          <i class="fa fa-redo"></i> {{ $t('refresh') || 'Refresh' }}
        </base-button>
        <base-button type="link" @click="trackCountModalShow = false">
          {{ $t('close') || 'Close' }}
        </base-button>
      </template>
    </modal>

    <!-- Fix Order Status modal -->
    <modal :show.sync="fixStatusModalShow" modal-classes="modal-secondary">
      <h3 slot="header" class="modal-title">
        {{ $t('reprocess.fix_status.title') || 'แก้ไขสถานะ Order' }}
      </h3>
      <div v-if="!fixStatusJobState">
        <p class="text-muted small mb-3">{{ $t('reprocess.fix_status.subtitle') || 'เปลี่ยนสถานะ order เป็น สำเร็จ (130)' }}</p>

        <!-- Scope selector -->
        <div class="mb-3">
          <el-radio-group v-model="fixStatusScope">
            <el-radio label="selected">
              {{ $t('reprocess.fix_status.scope_selected') || 'เฉพาะที่เลือก' }} ({{ selected.length }})
            </el-radio>
            <el-radio label="filter">
              {{ $t('reprocess.fix_status.scope_filtered') || 'ทั้งหมดตาม filter ปัจจุบัน' }}
            </el-radio>
          </el-radio-group>
        </div>

        <!-- Source statuses (shown only when scope=filter) -->
        <div class="mb-3" v-if="fixStatusScope === 'filter'">
          <label class="form-control-label">
            {{ $t('reprocess.fix_status.source_statuses_label') || 'สถานะต้นทางที่อนุญาต' }}
          </label>
          <div>
            <el-checkbox
              v-for="opt in fixStatusAllowedSources"
              :key="opt.value"
              v-model="opt.checked"
              class="mr-3 mb-1">
              {{ opt.label }}
            </el-checkbox>
          </div>
        </div>

      </div>

      <!-- Progress view (job started) -->
      <div v-else>
        <job-progress-view :state="fixStatusJobState"></job-progress-view>
      </div>

      <template slot="footer">
        <template v-if="!fixStatusJobState">
          <base-button type="link" @click="fixStatusModalShow = false">
            {{ $t('cancel') || 'Cancel' }}
          </base-button>
          <base-button
            type="danger"
            :loading="fixStatusSubmitting"
            :disabled="fixStatusSubmitDisabled"
            @click="submitFixStatus">
            {{ $t('reprocess.fix_status.submit') || 'ยืนยันและดำเนินการ' }}
          </base-button>
        </template>
        <template v-else>
          <base-button
            type="link"
            :disabled="fixStatusJobState && fixStatusJobState.running"
            @click="closeFixStatusModal">
            {{ $t('close') || 'Close' }}
          </base-button>
        </template>
      </template>
    </modal>

    <!-- Subscribe Thaipost modal -->
    <modal :show.sync="subscribeModalShow" modal-classes="modal-secondary">
      <h3 slot="header" class="modal-title">
        {{ $t('reprocess.thaipost_subscribe.title') || 'Subscribe Tracking (Thaipost)' }}
      </h3>
      <div v-if="!subscribeJobState">
        <p class="text-muted small mb-3">{{ $t('reprocess.thaipost_subscribe.subtitle') || 'เฉพาะ order ที่ shipping_provider=Thaipost และมี tracking number' }}</p>

        <!-- Scope selector -->
        <div class="mb-3">
          <el-radio-group v-model="subscribeScope">
            <el-radio label="selected">
              {{ $t('reprocess.thaipost_subscribe.scope_selected') || 'เฉพาะที่เลือก' }} ({{ selected.length }})
            </el-radio>
            <el-radio label="filter">
              {{ $t('reprocess.thaipost_subscribe.scope_filtered') || 'ทั้งหมดตาม filter ปัจจุบัน' }}
            </el-radio>
          </el-radio-group>
        </div>

      </div>

      <!-- Progress view (job started) -->
      <div v-else>
        <job-progress-view :state="subscribeJobState"></job-progress-view>
      </div>

      <template slot="footer">
        <template v-if="!subscribeJobState">
          <base-button type="link" @click="subscribeModalShow = false">
            {{ $t('cancel') || 'Cancel' }}
          </base-button>
          <base-button
            type="default"
            :loading="subscribeSubmitting"
            :disabled="subscribeSubmitDisabled"
            @click="submitSubscribe">
            {{ $t('reprocess.thaipost_subscribe.submit') || 'ยืนยันและดำเนินการ' }}
          </base-button>
        </template>
        <template v-else>
          <base-button
            type="link"
            :disabled="subscribeJobState && subscribeJobState.running"
            @click="closeSubscribeModal">
            {{ $t('close') || 'Close' }}
          </base-button>
        </template>
      </template>
    </modal>
  </div>
</template>

<script>
// Reprocess (refresh tracking from carrier) for orders that are not in
// a terminal state. Reuses the existing Order list endpoint with a
// status[] filter, then calls the per-order tracking-refresh endpoint
// from the row / select-all action.
//
// Also exposes two admin-only bulk actions:
//  - Fix Order Status: force orders from non-final statuses -> 130 Completed
//  - Subscribe Thaipost: subscribe tracking for Thaipost orders

import RouteBreadcrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { Table, TableColumn, Select, Option, DatePicker, Pagination, Radio, RadioGroup, Checkbox } from "element-ui";
import { OrderService } from "@/resource";
import Modal from "@/components/Modal";
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import OrderFixStatusBatchProvider from "@/resource/order_fix_status_batch";
import OrderThaipostSubscribeBatchProvider from "@/resource/order_thaipost_subscribe_batch";

const FixStatusBatchService = new OrderFixStatusBatchProvider();
const ThaipostSubscribeBatchService = new OrderThaipostSubscribeBatchProvider();

// Default statuses prefilled in the filter. The dropdown still lets
// staff add the other non-terminal codes if they need to. Picked to
// match the cases where a carrier refresh actually moves the needle —
// pre-pickup (115), in-transit (121), delivered-awaiting-COD (122),
// and returning (143).
const DEFAULT_REPROCESS_STATUSES = [115, 121, 122, 143];

// Full non-terminal set — used as a server-side safety net when the
// filter is empty.
const NON_FINAL_STATUSES = [100, 111, 112, 114, 115, 121, 122, 142, 143];

// Allowed source statuses for Fix Order Status (per spec).
// 130/140/141/142/143 are excluded — the BE rejects them anyway.
const FIX_STATUS_ALLOWED = [
  { value: 100, label: "รอชำระเงิน (100)", checked: false },
  { value: 111, label: "รอตัดรอบส่งสินค้า (111)", checked: false },
  { value: 112, label: "รอแพ็คสินค้า (112)", checked: false },
  { value: 114, label: "ส่งบางส่วน (114)", checked: false },
  { value: 115, label: "รอขนส่งรับพัสดุ (115)", checked: true },
  { value: 121, label: "กำลังขนส่ง (121)", checked: true },
  { value: 122, label: "จัดส่งสำเร็จ (122)", checked: true },
];

// Helper for the initial createdRange. Last 7 days inclusive of today.
function defaultCreatedRange() {
  var fmt = function(d) {
    return d.getFullYear() + "-" + String(d.getMonth() + 1).padStart(2, "0") + "-" + String(d.getDate()).padStart(2, "0");
  };
  var end = new Date();
  var start = new Date();
  start.setDate(end.getDate() - 6);
  return [fmt(start), fmt(end)];
}

// Inline job progress component (avoids a separate file / import).
var JobProgressView = {
  name: "job-progress-view",
  props: {
    state: { type: Object, default: null },
  },
  computed: {
    // BE state shapes differ across batch types:
    //   tracking-refresh uses processed/success/fail/current_id
    //   fix-status + thaipost-subscribe use done/succeeded/failed
    // Read both shapes so the same view works for all.
    doneCount: function() {
      if (!this.state) return 0;
      return this.state.done || this.state.processed || 0;
    },
    successCount: function() {
      if (!this.state) return 0;
      return this.state.succeeded || this.state.success || 0;
    },
    failCount: function() {
      if (!this.state) return 0;
      return this.state.failed || this.state.fail || 0;
    },
    skippedCount: function() {
      if (!this.state) return 0;
      return this.state.skipped || 0;
    },
    // Of the skipped rows, the ones held back because a return episode is live
    // on them. Broken out from `skipped` on purpose: "ข้ามไป" already counted
    // rows whose source status was not in the allow-list, so a single number
    // could not tell an operator whether a parcel is coming back.
    returningSkippedCount: function() {
      if (!this.state) return 0;
      return this.state.skipped_returning || 0;
    },
    totalCount: function() {
      if (!this.state) return 0;
      return this.state.total || 0;
    },
    percent: function() {
      if (!this.totalCount) return 0;
      return Math.min(100, Math.round((this.doneCount / this.totalCount) * 100));
    },
    remaining: function() {
      return Math.max(0, this.totalCount - this.doneCount);
    },
    isRunning: function() {
      return this.state && this.state.running;
    },
    isFinished: function() {
      return this.state && this.state.finished_at;
    },
  },
  // vue-cli production build uses the runtime-only Vue and silently drops
  // inline template strings — they would never compile. Hand-write a
  // render function so the component renders in every build mode.
  render: function(h) {
    var self = this;
    var s = this.state || {};

    function col(label, valueClass, value) {
      return h("div", { class: "col" }, [
        h("small", { class: "text-muted d-block" + (valueClass ? " " + valueClass : "") }, label),
        h("h4", valueClass ? { class: valueClass } : {}, String(value)),
      ]);
    }

    var children = [
      h("div", { class: "progress mb-3", style: "height:24px;background:#e9ecef;" }, [
        h(
          "div",
          {
            class: "progress-bar progress-bar-striped progress-bar-animated bg-primary",
            attrs: { role: "progressbar" },
            style: "width:" + (self.percent || (self.isRunning ? 5 : 0)) + "%",
          },
          self.percent + "%"
        ),
      ]),
      h("div", { class: "row text-center" }, [
        col("ทั้งหมด", "", self.totalCount),
        col("ดำเนินการแล้ว", "", self.doneCount),
        col("คงเหลือ", "text-warning", self.remaining),
        col("สำเร็จ", "text-success", self.successCount),
        col("ข้ามไป", "text-secondary", self.skippedCount),
        col("ตีกลับอยู่", "text-warning", self.returningSkippedCount),
        col("ล้มเหลว", "text-danger", self.failCount),
      ]),
    ];

    if (self.isRunning) {
      children.push(
        h("div", { class: "text-muted mt-2 text-center" }, [
          h("i", { class: "fa fa-spinner fa-spin" }),
          " กำลังประมวลผล...",
        ])
      );
    } else if (self.isFinished) {
      children.push(
        h("div", { class: "text-success mt-2 text-center" }, [
          h("i", { class: "fa fa-check-circle" }),
          " เสร็จสิ้น",
        ])
      );
    }

    if (s.last_error) {
      children.push(
        h("div", { class: "alert alert-danger mt-3 mb-0 py-2 small" }, s.last_error)
      );
    }

    // Skipped rows are NOT errors, so they get their own non-danger alert.
    //
    // fix-status-batch refuses to force an order to 130 สำเร็จ while a return
    // episode is live on it (return_status = 1) — the parcel is on its way
    // back, and 130 would stamp completed_at, which the commission report
    // reads. The backend used to put that reason in `last_error`, which made a
    // healthy job render as failed; it now sends `last_skip_note` as a BARE
    // i18n key plus the arguments below, so the reason has to be translated
    // here or it reaches the operator as nothing at all.
    //
    // Keys: errors.fix_status.skipped_return_in_transit
    //       errors.fix_status.skipped_changed_underneath
    if (s.last_skip_note) {
      children.push(
        h(
          "div",
          { class: "alert alert-warning mt-3 mb-0 py-2 small" },
          self.$t(s.last_skip_note, {
            count: s.skipped_returning || 0,
            order: s.last_skip_order_id || "",
          })
        )
      );
    }

    return h("div", children);
  },
};

export default {
  name: "order-reprocess",
  components: {
    RouteBreadcrumb,
    Modal,
    JobProgressView,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker,
    [Pagination.name]: Pagination,
    [Radio.name]: Radio,
    [RadioGroup.name]: RadioGroup,
    [Checkbox.name]: Checkbox,
  },
  computed: {
    ...mapGetters({
      isAdmin: "Auth/isAdmin",
    }),
    from: function() {
      return (this.page - 1) * this.perPage;
    },
    to: function() {
      return Math.min(this.page * this.perPage, this.total);
    },
    batchRunning: function() {
      return !!(this.batchState && this.batchState.running);
    },
    batchPercent: function() {
      if (!this.batchState || !this.batchState.total) return 0;
      return Math.min(100, Math.round((this.batchState.processed / this.batchState.total) * 100));
    },
    batchRemaining: function() {
      if (!this.batchState) return 0;
      return Math.max(0, (this.batchState.total || 0) - (this.batchState.processed || 0));
    },
    // Thaipost quota meter colour cuts at 70% (warning) and 90% (danger).
    trackCountPercent: function() {
      if (!this.trackCount || !this.trackCount.track_count_limit) return 0;
      return Math.min(100, Math.round((this.trackCount.count_number / this.trackCount.track_count_limit) * 100));
    },
    // Fix Status helpers
    fixStatusCheckedValues: function() {
      return this.fixStatusAllowedSources.filter(function(o) { return o.checked; }).map(function(o) { return o.value; });
    },
    fixStatusSubmitDisabled: function() {
      if (this.fixStatusSubmitting) return true;
      if (this.fixStatusScope === "selected" && this.selected.length === 0) return true;
      if (this.fixStatusScope === "filter" && this.fixStatusCheckedValues.length === 0) return true;
      return false;
    },
    // Subscribe helpers
    subscribeSubmitDisabled: function() {
      if (this.subscribeSubmitting) return true;
      if (this.subscribeScope === "selected" && this.selected.length === 0) return true;
      return false;
    },
  },
  data: function() {
    return {
      loading: false,
      bulkRunning: false,
      rows: [],
      selected: [],
      rowLoading: {},
      page: 1,
      perPage: 50,
      total: 0,
      filters: {
        // Default to the 4 high-value codes — staff can add more from
        // the dropdown when they need wider coverage.
        statusList: DEFAULT_REPROCESS_STATUSES.slice(),
        // Default to Madamefin (company) since most carrier refreshes
        // target self-shipped orders.
        platformID: 0,
        // 0 = all carriers; matches the shipping_provider_id constants
        // in controllers/const.go (1=Thaipost, 2=Kerry, 7=SPX, ...).
        shippingProviderID: 0,
        // Default to the last 7 days so the initial table is bounded
        // — without a date filter the query sweeps every non-terminal
        // order ever, which can be tens of thousands of rows.
        createdRange: defaultCreatedRange(),
      },
      // Batch (server-side process-by-filter) state. Modal opens on
      // startBatch and stays open until the user closes it; polling
      // runs every 2 s until state.running === false.
      batchModalShow: false,
      batchState: null,
      batchPollTimer: null,
      // Thaipost daily-quota popover state. {track_date, count_number,
      // track_count_limit, updated_at} from the cache endpoint.
      trackCountModalShow: false,
      trackCount: null,
      trackCountLoading: false,
      // Status options for the multi-select. Labels in Thai to match
      // the rest of the BO; numeric code in parens for staff who
      // memorise codes.
      statusOptions: [
        { value: 100, label: "รอชำระเงิน (100)" },
        { value: 111, label: "รอตัดรอบส่งสินค้า (111)" },
        { value: 112, label: "รอแพ็คสินค้า (112)" },
        { value: 114, label: "ส่งบางส่วน (114)" },
        { value: 115, label: "รอขนส่งรับพัสดุ (115)" },
        { value: 121, label: "กำลังขนส่ง (121)" },
        { value: 122, label: "จัดส่งสำเร็จ (122)" },
        { value: 142, label: "เคลม (142)" },
        { value: 143, label: "กำลังตีกลับ (143)" },
      ],

      // ---- Fix Order Status modal state ----
      fixStatusModalShow: false,
      fixStatusScope: "selected",   // "selected" | "filter"
      // Deep-copy so checkbox state is mutable and independent per open
      fixStatusAllowedSources: FIX_STATUS_ALLOWED.map(function(o) { return Object.assign({}, o); }),
      fixStatusSubmitting: false,
      fixStatusJobState: null,
      fixStatusPollTimer: null,

      // ---- Subscribe Thaipost modal state ----
      subscribeModalShow: false,
      subscribeScope: "selected",   // "selected" | "filter"
      subscribeSubmitting: false,
      subscribeJobState: null,
      subscribePollTimer: null,
    };
  },
  mounted: function() {
    this.loadData();
    // If a batch is already running (started by another tab / refresh),
    // re-attach the progress modal instead of letting the user fire a
    // duplicate.
    this.checkExistingBatch();
  },
  beforeDestroy: function() {
    this.stopBatchPolling();
    this.stopFixStatusPolling();
    this.stopSubscribePolling();
  },
  methods: {
    // Format an ISO timestamp ("2026-05-20T16:50:46+07:00") as the BO's
    // conventional "yyyy-MM-dd HH:mm:ss" so the column doesn't show the
    // raw RFC3339 with timezone offset. Falls back to empty for null /
    // unparseable inputs.
    formatDateTime: function(v) {
      if (!v) return "";
      var d = new Date(v);
      if (isNaN(d.getTime())) return String(v);
      var pad = function(n) { return String(n).padStart(2, "0"); };
      return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate()) + " " + pad(d.getHours()) + ":" + pad(d.getMinutes()) + ":" + pad(d.getSeconds());
    },
    // Opens the quota modal and reads the cached snapshot. The cache is
    // updated automatically by every refresh / batch / poll call, so
    // the value is "free" (no extra quota burn) but can lag if there's
    // been no Thaipost traffic since the last day-rollover.
    showTrackCount: async function() {
      this.trackCountModalShow = true;
      this.trackCountLoading = true;
      try {
        var res = await OrderService.ThaipostTrackCount();
        this.trackCount = (res && res.data && res.data.Datum) || null;
      } catch (e) {
        this.trackCount = null;
      } finally {
        this.trackCountLoading = false;
      }
    },
    statusLabel: function(s) {
      var map = {
        100: "รอชำระเงิน",
        111: "รอตัดรอบส่งสินค้า",
        112: "รอแพ็คสินค้า",
        114: "ส่งบางส่วน",
        115: "รอขนส่งรับพัสดุ",
        121: "กำลังขนส่ง",
        122: "จัดส่งสำเร็จ",
        130: "สำเร็จ",
        140: "ยกเลิก",
        141: "ตีกลับ",
        142: "เคลม",
        143: "กำลังตีกลับ",
      };
      return map[Number(s)] || String(s);
    },
    loadData: async function() {
      this.loading = true;
      try {
        var params = {
          page: this.page,
          per_page: this.perPage,
        };
        var statuses = this.filters.statusList.length > 0 ? this.filters.statusList : NON_FINAL_STATUSES;
        // Axios default serializer wraps arrays with [], and using a
        // key that already ends in [] double-wraps to order_status[][]=
        // which the backend can't read. Send the plain key with an array
        // value — the backend accepts both `order_status` and `order_status[]`.
        params.order_status = statuses;
        if (this.filters.platformID) {
          params.bo_platform_id = this.filters.platformID;
        }
        if (this.filters.shippingProviderID) {
          params.shipping_provider_id = this.filters.shippingProviderID;
        }
        if (this.filters.createdRange && this.filters.createdRange.length === 2) {
          if (this.filters.createdRange[0]) params.time_from = this.filters.createdRange[0];
          if (this.filters.createdRange[1]) params.time_to = this.filters.createdRange[1];
        }
        // Use the dedicated lean+paginated endpoint — the generic
        // /orders endpoint returns up to 2 000 full rows and the
        // browser stalls trying to render them all. Response shape is
        // {Status, Datum: {items, page, per_page, total}} because the
        // backend uses r.Set() rather than r.Append().
        var res = await OrderService.ReprocessList(params);
        var datum = (res.data && res.data.Datum) || {};
        this.rows = datum.items || [];
        this.total = datum.total || 0;
        if (datum.page) this.page = datum.page;
        if (datum.per_page) this.perPage = datum.per_page;
      } catch (err) {
        console.log(err);
      }
      this.loading = false;
    },
    onSelectionChange: function(rows) {
      this.selected = rows;
    },
    refreshRow: async function(row) {
      this.$set(this.rowLoading, row.ID, true);
      try {
        await OrderService.TrackingRefresh(row.ID);
        // Optimistically reload — the row's status / shipping_status may have moved.
        await this.loadData();
        swal.fire({
          title: this.$t("reprocess.refresh_ok") || "Refreshed",
          icon: "success",
          buttonsStyling: false,
          confirmButtonClass: "btn btn-success btn-fill",
        });
      } catch (err) {
        var msg = (err.response && err.response.data && (err.response.data.Cause || err.response.data.Msg)) || "Refresh failed";
        swal.fire({
          title: msg,
          icon: "error",
          buttonsStyling: false,
          confirmButtonClass: "btn btn-danger btn-fill",
        });
      }
      this.$set(this.rowLoading, row.ID, false);
    },
    // Build the filter payload that the backend batch endpoint accepts.
    // Mirrors what loadData() sends to /orders but with explicit field
    // names the backend job stores in Redis.
    batchFilterPayload: function() {
      var statuses = this.filters.statusList.length > 0 ? this.filters.statusList : NON_FINAL_STATUSES;
      var payload = { status_list: statuses };
      if (this.filters.platformID) payload.bo_platform_id = this.filters.platformID;
      if (this.filters.shippingProviderID) payload.shipping_provider_id = this.filters.shippingProviderID;
      if (this.filters.createdRange && this.filters.createdRange.length === 2) {
        if (this.filters.createdRange[0]) payload.time_from = this.filters.createdRange[0];
        if (this.filters.createdRange[1]) payload.time_to = this.filters.createdRange[1];
      }
      return payload;
    },
    // Build the shared filter object sent to fix-status and subscribe batch endpoints.
    genericFilterPayload: function() {
      var statuses = this.filters.statusList.length > 0 ? this.filters.statusList : NON_FINAL_STATUSES;
      var payload = { status_list: statuses };
      if (this.filters.platformID) payload.bo_platform_id = this.filters.platformID;
      if (this.filters.shippingProviderID) payload.shipping_provider_id = this.filters.shippingProviderID;
      if (this.filters.createdRange && this.filters.createdRange.length === 2) {
        if (this.filters.createdRange[0]) payload.time_from = this.filters.createdRange[0];
        if (this.filters.createdRange[1]) payload.time_to = this.filters.createdRange[1];
      }
      return payload;
    },
    checkExistingBatch: async function() {
      try {
        var res = await OrderService.TrackingRefreshBatchStatus();
        var state = res && res.data && res.data.Datum;
        if (state && state.running) {
          this.batchState = state;
          this.batchModalShow = true;
          this.startBatchPolling();
        }
      } catch (e) { /* idle is fine */ }
    },
    startBatch: async function() {
      var confirm = await swal.fire({
        title: this.$t("reprocess.batch_confirm_title") || "Process all matching?",
        text: this.$t("reprocess.batch_confirm_text") || "The backend will iterate every order matching the current filter.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: this.$t("yes") || "Yes",
        cancelButtonText: this.$t("cancel") || "Cancel",
        buttonsStyling: false,
        confirmButtonClass: "btn btn-warning btn-fill",
        cancelButtonClass: "btn btn-link",
      });
      if (!confirm.value) return;

      try {
        var res = await OrderService.TrackingRefreshBatchStart(this.batchFilterPayload());
        this.batchState = (res && res.data && res.data.Datum) || null;
        this.batchModalShow = true;
        this.startBatchPolling();
      } catch (err) {
        console.log(err);
        var msg = (err.response && err.response.data && (err.response.data.Cause || err.response.data.Msg)) || "Failed to start batch";
        swal.fire({
          title: msg,
          icon: "error",
          buttonsStyling: false,
          confirmButtonClass: "btn btn-danger btn-fill",
        });
      }
    },
    startBatchPolling: function() {
      this.stopBatchPolling();
      var self = this;
      this.batchPollTimer = setInterval(async function() {
        try {
          var res = await OrderService.TrackingRefreshBatchStatus();
          var state = res && res.data && res.data.Datum;
          if (state) {
            self.batchState = state;
            if (!state.running) {
              self.stopBatchPolling();
              // Refresh the table once the job is done so the user sees
              // the carrier-updated rows without a manual reload.
              self.loadData();
            }
          }
        } catch (e) { console.log(e); }
      }, 2000);
    },
    stopBatchPolling: function() {
      if (this.batchPollTimer) {
        clearInterval(this.batchPollTimer);
        this.batchPollTimer = null;
      }
    },
    closeBatchModal: function() {
      if (this.batchState && this.batchState.running) return; // guard
      this.batchModalShow = false;
      this.stopBatchPolling();
    },
    refreshSelected: async function() {
      if (this.selected.length === 0) return;
      // Refresh-selected and Process-all share the same progress modal
      // now — we just pass the picked IDs through the batch endpoint
      // (filter is ignored when ids[] is non-empty). Same modal, same
      // polling, consistent UX.
      var ids = this.selected.map(function(r) { return r.ID; });
      try {
        var res = await OrderService.TrackingRefreshBatchStart({ ids: ids });
        this.batchState = (res && res.data && res.data.Datum) || null;
        this.batchModalShow = true;
        this.startBatchPolling();
      } catch (err) {
        console.log(err);
        var msg = (err.response && err.response.data && (err.response.data.Cause || err.response.data.Msg)) || "Failed to start refresh";
        swal.fire({
          title: msg,
          icon: "error",
          buttonsStyling: false,
          confirmButtonClass: "btn btn-danger btn-fill",
        });
      }
    },

    // ---- Fix Order Status ----

    openFixStatusModal: function() {
      // Reset state
      this.fixStatusScope = "selected";
      this.fixStatusAllowedSources = FIX_STATUS_ALLOWED.map(function(o) { return Object.assign({}, o); });
      this.fixStatusSubmitting = false;
      this.fixStatusJobState = null;
      this.stopFixStatusPolling();
      this.fixStatusModalShow = true;
    },
    submitFixStatus: async function() {
      this.fixStatusSubmitting = true;
      try {
        // source_statuses is required by BE in both scope modes — for "ids"
        // it acts as a per-order skip filter (orders not in the picked set
        // are reported as skipped, not failed). For "filter" it also shapes
        // the WHERE clause when no explicit status_list is in the filter.
        var payload = {
          source_statuses: this.fixStatusCheckedValues,
        };
        if (this.fixStatusScope === "selected") {
          payload.ids = this.selected.map(function(r) { return r.ID; });
        } else {
          payload.filter = this.genericFilterPayload();
        }
        console.log("[fix-status] POST payload", payload);
        var res = await FixStatusBatchService.start(payload);
        console.log("[fix-status] POST response", res && res.data);
        var datum = (res && res.data && res.data.Datum) || null;
        // Defensive: always set a non-null state so the v-else branch in the
        // modal renders something; the progress view falls back to an
        // animated "starting" stub when total/done aren't there yet.
        this.fixStatusJobState = datum || { running: true, total: 0, done: 0, succeeded: 0, skipped: 0, failed: 0 };
        // BE keys batch state by enterprise (one active job per enterprise),
        // so :job_id in the polling path is decorative. Treat any non-null
        // datum as a started job and poll with a placeholder.
        this.startFixStatusPolling("current");
      } catch (err) {
        console.log(err);
        var msg = (err.response && err.response.data && (err.response.data.Cause || err.response.data.Msg)) || "Failed to start fix-status batch";
        swal.fire({
          title: msg,
          icon: "error",
          buttonsStyling: false,
          confirmButtonClass: "btn btn-danger btn-fill",
        });
      } finally {
        this.fixStatusSubmitting = false;
      }
    },
    startFixStatusPolling: function(jobId) {
      this.stopFixStatusPolling();
      var self = this;
      this.fixStatusPollTimer = setInterval(async function() {
        try {
          var res = await FixStatusBatchService.pollStatus(jobId);
          var state = res && res.data && res.data.Datum;
          if (state) {
            self.fixStatusJobState = state;
            if (!state.running) {
              self.stopFixStatusPolling();
              self.loadData();
            }
          }
        } catch (e) {
          // 404 means job expired from Redis — treat as finished
          if (e.response && e.response.status === 404) {
            self.stopFixStatusPolling();
          }
        }
      }, 2500);
    },
    stopFixStatusPolling: function() {
      if (this.fixStatusPollTimer) {
        clearInterval(this.fixStatusPollTimer);
        this.fixStatusPollTimer = null;
      }
    },
    closeFixStatusModal: function() {
      if (this.fixStatusJobState && this.fixStatusJobState.running) return;
      this.fixStatusModalShow = false;
      this.stopFixStatusPolling();
    },

    // ---- Subscribe Thaipost ----

    openSubscribeModal: function() {
      // Reset state
      this.subscribeScope = "selected";
      this.subscribeSubmitting = false;
      this.subscribeJobState = null;
      this.stopSubscribePolling();
      this.subscribeModalShow = true;
    },
    submitSubscribe: async function() {
      this.subscribeSubmitting = true;
      try {
        var payload = {};
        if (this.subscribeScope === "selected") {
          payload.ids = this.selected.map(function(r) { return r.ID; });
        } else {
          payload.filter = this.genericFilterPayload();
        }
        console.log("[subscribe] POST payload", payload);
        var res = await ThaipostSubscribeBatchService.start(payload);
        console.log("[subscribe] POST response", res && res.data);
        var datum = (res && res.data && res.data.Datum) || null;
        // Defensive: same stub fallback as fix-status so the modal never
        // sees a null state mid-flight.
        this.subscribeJobState = datum || { running: true, total: 0, done: 0, succeeded: 0, skipped: 0, failed: 0 };
        // BE keys state per-enterprise; :job_id in polling URL is decorative.
        this.startSubscribePolling("current");
      } catch (err) {
        console.log(err);
        var msg = (err.response && err.response.data && (err.response.data.Cause || err.response.data.Msg)) || "Failed to start subscribe batch";
        swal.fire({
          title: msg,
          icon: "error",
          buttonsStyling: false,
          confirmButtonClass: "btn btn-danger btn-fill",
        });
      } finally {
        this.subscribeSubmitting = false;
      }
    },
    startSubscribePolling: function(jobId) {
      this.stopSubscribePolling();
      var self = this;
      this.subscribePollTimer = setInterval(async function() {
        try {
          var res = await ThaipostSubscribeBatchService.pollStatus(jobId);
          var state = res && res.data && res.data.Datum;
          if (state) {
            self.subscribeJobState = state;
            if (!state.running) {
              self.stopSubscribePolling();
              self.loadData();
            }
          }
        } catch (e) {
          if (e.response && e.response.status === 404) {
            self.stopSubscribePolling();
          }
        }
      }, 2500);
    },
    stopSubscribePolling: function() {
      if (this.subscribePollTimer) {
        clearInterval(this.subscribePollTimer);
        this.subscribePollTimer = null;
      }
    },
    closeSubscribeModal: function() {
      if (this.subscribeJobState && this.subscribeJobState.running) return;
      this.subscribeModalShow = false;
      this.stopSubscribePolling();
    },
  },
};
</script>
